import {
  TEMPLATE_MAIN_TEXT_LAYER_ID,
  TEMPLATE_SHAPE_LAYER_ID,
  TEMPLATE_SUB_TEXT_LAYER_ID,
  layerIdToPrimitiveId,
  normalizePresetLayerOrder,
  primitiveLayerId,
} from '../presets/presetLayerUtils';
import {
  getDurationsForConfig,
  getRenderSceneMetrics,
  resolvePrimitiveFillColor,
  resolvePrimitiveStrokeColor,
  traceParallelogramPath,
} from './renderSceneMetrics';

const TEMPLATE_PRESET_LOGO_SRC = '/preset-logo-photo-icon.svg';

const roundRect = (ctx, x, y, w, h, r, color, mode = 'fill') => {
  const radius = Math.min(Math.max(r, 0), Math.min(w, h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  if (mode === 'stroke') {
    ctx.strokeStyle = color;
    ctx.stroke();
    return;
  }
  ctx.fillStyle = color;
  ctx.fill();
};

const applyFadeCurve = (t, curve) => {
  const clamped = Math.min(Math.max(t, 0), 1);
  if (curve === 'ease-in') return clamped * clamped * clamped;
  if (curve === 'ease-out') return 1 - Math.pow(1 - clamped, 3);
  return clamped;
};

const drawReadableText = (ctx, text, x, y, width, mirrored) => {
  if (!mirrored) {
    ctx.fillText(text, x, y);
    return;
  }
  ctx.save();
  ctx.translate(x + width, 0);
  ctx.scale(-1, 1);
  ctx.fillText(text, 0, y);
  ctx.restore();
};

const drawReadableImage = (ctx, image, x, y, width, height, mirrored) => {
  if (!mirrored) {
    ctx.drawImage(image, x, y, width, height);
    return;
  }
  ctx.save();
  ctx.translate(x + width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(image, 0, y, width, height);
  ctx.restore();
};

export const createLowerThirdRenderer = () => {
  const imageCache = new Map();
  let scratchCanvas = null;
  let scratchContext = null;

  const getCachedImage = (imageSrc) => {
    if (!imageSrc || typeof Image === 'undefined') return null;
    const existing = imageCache.get(imageSrc);
    if (existing?.status === 'loaded') return existing.image;
    if (existing?.status === 'loading') return null;
    const image = new Image();
    imageCache.set(imageSrc, { status: 'loading', image: null });
    image.onload = () => imageCache.set(imageSrc, { status: 'loaded', image });
    image.onerror = () => imageCache.set(imageSrc, { status: 'error', image: null });
    image.src = imageSrc;
    return null;
  };

  const getScratch = (width, height) => {
    if (!scratchCanvas && typeof document !== 'undefined') scratchCanvas = document.createElement('canvas');
    if (!scratchCanvas) return null;
    if (!scratchContext) scratchContext = scratchCanvas.getContext('2d');
    if (!scratchContext) return null;
    if (scratchCanvas.width !== width) scratchCanvas.width = width;
    if (scratchCanvas.height !== height) scratchCanvas.height = height;
    return { canvas: scratchCanvas, ctx: scratchContext };
  };

  const drawFrame = (ctx, frame, person, config, options = {}) => {
    const canvasWidth = options.canvasWidth || ctx.canvas.width;
    const canvasHeight = options.canvasHeight || ctx.canvas.height;
    const renderConfig = options.forceTransparentBackground ? { ...config, backgroundMode: 'alpha' } : config;
    const durations = getDurationsForConfig(options.fps || 60, renderConfig);
    const metrics = getRenderSceneMetrics({ ctx, person, config: renderConfig, activePresetId: options.activePresetId });
    const isMirrored = person?.mirrored === true;
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    if (renderConfig.backgroundMode === 'green') {
      ctx.fillStyle = renderConfig.backgroundKeyColor || '#00FF00';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    let drawX = renderConfig.posX;
    const drawY = renderConfig.posY;
    let fadeProgress = 1;
    if (!options.staticStill) {
      const easeOut = (t) => 1 - ((1 - t) ** 3);
      const easeIn = (t) => t ** 3;
      const atExit = frame >= durations.transitionInFrames + durations.stillFrames;
      const slideOvershoot = 120 * metrics.scale;
      const startXLeft = -metrics.renderBounds.maxX - slideOvershoot;
      const startXRight = canvasWidth - metrics.renderBounds.minX + slideOvershoot;
      const entryProgress = Math.min(1, frame / durations.transitionInFrames);
      const exitProgress = atExit ? Math.min(1, (frame - durations.transitionInFrames - durations.stillFrames) / durations.transitionOutFrames) : 0;
      if (frame < durations.transitionInFrames) {
        if (renderConfig.transitionInType === 'slide-left') drawX = startXLeft + (renderConfig.posX - startXLeft) * easeOut(entryProgress);
        else if (renderConfig.transitionInType === 'slide-right') drawX = startXRight + (renderConfig.posX - startXRight) * easeOut(entryProgress);
      } else if (frame < durations.totalFrames) {
        if (renderConfig.transitionOutType === 'slide-left') drawX = renderConfig.posX + (startXLeft - renderConfig.posX) * easeIn(exitProgress);
        else if (renderConfig.transitionOutType === 'slide-right') drawX = renderConfig.posX + (startXRight - renderConfig.posX) * easeIn(exitProgress);
      }
      if ((renderConfig.fadeIn || renderConfig.transitionInType === 'fade') && frame < durations.fadeInFrames) {
        fadeProgress = applyFadeCurve(frame / durations.fadeInFrames, renderConfig.fadeInCurve || 'linear');
      }
      if ((renderConfig.fadeOut || renderConfig.transitionOutType === 'fade') && atExit) {
        const fadeStart = durations.transitionInFrames + durations.stillFrames + Math.max(0, durations.transitionOutFrames - durations.fadeOutFrames);
        const fadeFrame = frame - fadeStart;
        if (fadeFrame >= 0 && fadeFrame < durations.fadeOutFrames) {
          fadeProgress = Math.min(fadeProgress, 1 - applyFadeCurve(fadeFrame / durations.fadeOutFrames, renderConfig.fadeOutCurve || 'linear'));
        } else if (frame >= fadeStart + durations.fadeOutFrames) {
          fadeProgress = 0;
        }
      }
    }

    const renderScene = (targetCtx) => {
      const drawTemplateShapeLayer = () => {
        if (!metrics.templateShapeVisible) return;
        if (renderConfig.preset === 'minimal-box') {
          roundRect(targetCtx, 0, 0, metrics.minimal.nameWidth, metrics.minimal.nameHeight, 0, renderConfig.colorPrimary);
          roundRect(targetCtx, 0, metrics.minimal.nameHeight, metrics.minimal.titleWidth, metrics.minimal.titleHeight, 0, renderConfig.colorSecondary);
          return;
        }
        if (renderConfig.preset === 'edge-link') {
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(metrics.edgeLink.barX, 0, metrics.edgeLink.barWidth, metrics.edgeLink.bodyHeight);
          traceParallelogramPath(targetCtx, metrics.edgeLink.tipX, 0, metrics.edgeLink.tipWidth, metrics.edgeLink.bodyHeight, metrics.edgeLink.tipSkewX);
          targetCtx.fill();
          targetCtx.fillStyle = renderConfig.colorPrimary;
          targetCtx.fillRect(metrics.edgeLink.accentX, 0, metrics.edgeLink.accentWidth, metrics.edgeLink.bodyHeight);
          return;
        }
        if (renderConfig.preset === 'logo-badge') {
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(metrics.logoBadge.bodyX, 0, metrics.logoBadge.bodyWidth, metrics.logoBadge.bodyHeight);
          roundRect(targetCtx, metrics.logoBadge.accentX, metrics.logoBadge.accentY, metrics.logoBadge.accentWidth, metrics.logoBadge.accentHeight, metrics.logoBadge.accentRadius, renderConfig.colorPrimary);
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(metrics.logoBadge.logoPlateX, metrics.logoBadge.logoPlateY, metrics.logoBadge.logoPlateSize, metrics.logoBadge.logoPlateSize);
          const logoImage = getCachedImage(TEMPLATE_PRESET_LOGO_SRC);
          if (logoImage) {
            drawReadableImage(
              targetCtx,
              logoImage,
              metrics.logoBadge.logoImageX,
              metrics.logoBadge.logoImageY,
              metrics.logoBadge.logoImageWidth,
              metrics.logoBadge.logoImageHeight,
              isMirrored,
            );
          }
          return;
        }
        if (renderConfig.preset === 'modern-accent') {
          targetCtx.save();
          targetCtx.transform(1, 0, -metrics.modern.skewFactor, 1, 0, 0);
          targetCtx.fillStyle = renderConfig.colorSecondary;
          targetCtx.fillRect(metrics.modern.skewOffset, 0, metrics.baseBoxWidth, metrics.baseBoxHeight);
          targetCtx.fillStyle = renderConfig.colorPrimary;
          targetCtx.fillRect(metrics.modern.accentOffset, 0, metrics.modern.accentWidth, metrics.baseBoxHeight);
          targetCtx.restore();
          return;
        }
        roundRect(targetCtx, 0, 0, metrics.baseBoxWidth, metrics.baseBoxHeight, metrics.classic.radius, renderConfig.colorSecondary);
        roundRect(targetCtx, 0, 0, metrics.classic.accentWidth, metrics.baseBoxHeight, metrics.classic.radius, renderConfig.colorPrimary);
      };

      const drawTemplateMainTextLayer = () => {
        if (!metrics.templateMainTextVisible) return;
        targetCtx.fillStyle = renderConfig.colorTextUpper;
        targetCtx.font = metrics.mainFont;
        if (renderConfig.preset === 'minimal-box') drawReadableText(targetCtx, metrics.mainText, metrics.minimal.textPaddingX, metrics.minimal.nameTextY, metrics.mainTextWidth, isMirrored);
        else if (renderConfig.preset === 'edge-link') drawReadableText(targetCtx, metrics.mainText, metrics.edgeLink.textX, metrics.edgeLink.mainTextY, metrics.mainTextWidth, isMirrored);
        else if (renderConfig.preset === 'logo-badge') drawReadableText(targetCtx, metrics.mainText, metrics.logoBadge.textX, metrics.logoBadge.mainTextY, metrics.mainTextWidth, isMirrored);
        else if (renderConfig.preset === 'modern-accent') drawReadableText(targetCtx, metrics.mainText, metrics.modern.textX, metrics.modern.mainTextY, metrics.mainTextWidth, isMirrored);
        else drawReadableText(targetCtx, metrics.mainText, metrics.classic.textPaddingX, metrics.classic.mainTextY, metrics.mainTextWidth, isMirrored);
      };

      const drawTemplateSubTextLayer = () => {
        if (!metrics.templateSubTextVisible) return;
        targetCtx.fillStyle = renderConfig.colorTextLower;
        targetCtx.font = metrics.subFont;
        if (renderConfig.preset === 'minimal-box') drawReadableText(targetCtx, metrics.subText, metrics.minimal.textPaddingX, metrics.minimal.titleTextY, metrics.subTextWidth, isMirrored);
        else if (renderConfig.preset === 'edge-link') drawReadableText(targetCtx, metrics.subText, metrics.edgeLink.textX, metrics.edgeLink.subTextY, metrics.subTextWidth, isMirrored);
        else if (renderConfig.preset === 'logo-badge') drawReadableText(targetCtx, metrics.subText, metrics.logoBadge.textX, metrics.logoBadge.subTextY, metrics.subTextWidth, isMirrored);
        else if (renderConfig.preset === 'modern-accent') drawReadableText(targetCtx, metrics.subText, metrics.modern.textX, metrics.modern.subTextY, metrics.subTextWidth, isMirrored);
        else drawReadableText(targetCtx, metrics.subText, metrics.classic.textPaddingX, metrics.classic.subTextY, metrics.subTextWidth, isMirrored);
      };

      const drawPrimitiveLayer = (shape) => {
        if (!shape || shape.visible === false) return;
        const fillColor = resolvePrimitiveFillColor(shape, renderConfig);
        const strokeColor = resolvePrimitiveStrokeColor(shape, renderConfig);
        const centerX = shape.baseBounds.minX + (shape.baseBounds.width / 2);
        const centerY = shape.baseBounds.minY + (shape.baseBounds.height / 2);
        targetCtx.save();
        targetCtx.translate(centerX, centerY);
        targetCtx.rotate((shape.rotation * Math.PI) / 180);
        targetCtx.translate(-centerX, -centerY);
        if (shape.type === 'image') {
          const imageObj = getCachedImage(shape.imageData);
          if (imageObj) {
            drawReadableImage(targetCtx, imageObj, shape.x, shape.y, shape.width, shape.height, isMirrored);
          }
        } else if (shape.layerKind !== 'text') {
          if (shape.type === 'line') {
            targetCtx.beginPath();
            targetCtx.moveTo(shape.x, shape.y);
            targetCtx.lineTo(shape.x + shape.width, shape.y);
            targetCtx.strokeStyle = strokeColor;
            targetCtx.lineWidth = shape.strokeWidth;
            targetCtx.lineCap = 'round';
            targetCtx.stroke();
          } else if (shape.type === 'circle') {
            const radius = shape.width / 2;
            targetCtx.beginPath();
            targetCtx.arc(shape.x + radius, shape.y + radius, radius, 0, Math.PI * 2);
            if (shape.fill) {
              targetCtx.fillStyle = fillColor;
              targetCtx.fill();
            }
            if (shape.stroke) {
              targetCtx.strokeStyle = strokeColor;
              targetCtx.lineWidth = shape.strokeWidth;
              targetCtx.stroke();
            }
          } else if (shape.type === 'parallelogram') {
            traceParallelogramPath(targetCtx, shape.x, shape.y, shape.width, shape.height, shape.skewX || 0);
            if (shape.fill) {
              targetCtx.fillStyle = fillColor;
              targetCtx.fill();
            }
            if (shape.stroke) {
              traceParallelogramPath(targetCtx, shape.x, shape.y, shape.width, shape.height, shape.skewX || 0);
              targetCtx.strokeStyle = strokeColor;
              targetCtx.lineWidth = shape.strokeWidth;
              targetCtx.stroke();
            }
          } else {
            if (shape.fill) roundRect(targetCtx, shape.x, shape.y, shape.width, shape.height, shape.type === 'roundedRect' ? shape.cornerRadius : 0, fillColor);
            if (shape.stroke) {
              targetCtx.lineWidth = shape.strokeWidth;
              roundRect(targetCtx, shape.x, shape.y, shape.width, shape.height, shape.type === 'roundedRect' ? shape.cornerRadius : 0, strokeColor, 'stroke');
            }
          }
        }
        if (shape.textLayout?.lines?.length) {
          for (const line of shape.textLayout.lines) {
            targetCtx.fillStyle = shape.layerKind === 'text' ? fillColor : (line.kind === 'main' ? renderConfig.colorTextUpper : renderConfig.colorTextLower);
            targetCtx.font = line.font;
            drawReadableText(targetCtx, line.text, line.x, line.y, line.width, isMirrored);
          }
        }
        targetCtx.restore();
      };

      targetCtx.save();
      if (isMirrored) {
        targetCtx.translate(canvasWidth, 0);
        targetCtx.scale(-1, 1);
      }
      targetCtx.translate(drawX + metrics.rotationPivot.x, drawY + metrics.rotationPivot.y);
      targetCtx.rotate(metrics.globalRotationRadians);
      targetCtx.translate(-metrics.rotationPivot.x, -metrics.rotationPivot.y);
      const primitiveLayerMap = new Map(metrics.primitives.map((shape) => [primitiveLayerId(shape.id), shape]));
      const renderLayerOrder = normalizePresetLayerOrder(renderConfig.layerOrder, renderConfig.shapePrimitives);
      for (const layerId of renderLayerOrder) {
        if (layerId === TEMPLATE_SHAPE_LAYER_ID) drawTemplateShapeLayer();
        else if (layerId === TEMPLATE_MAIN_TEXT_LAYER_ID) drawTemplateMainTextLayer();
        else if (layerId === TEMPLATE_SUB_TEXT_LAYER_ID) drawTemplateSubTextLayer();
        else if (layerIdToPrimitiveId(layerId)) drawPrimitiveLayer(primitiveLayerMap.get(layerId));
      }
      targetCtx.restore();
    };

    if (fadeProgress <= 0) return;
    if (fadeProgress >= 1) {
      renderScene(ctx);
      return;
    }
    const scratch = getScratch(canvasWidth, canvasHeight);
    if (!scratch?.ctx) {
      ctx.save();
      ctx.globalAlpha = fadeProgress;
      renderScene(ctx);
      ctx.restore();
      return;
    }
    scratch.ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    renderScene(scratch.ctx);
    ctx.save();
    ctx.globalAlpha = fadeProgress;
    ctx.drawImage(scratch.canvas, 0, 0, canvasWidth, canvasHeight);
    ctx.restore();
  };

  return { drawFrame, getDurationsForConfig };
};
